import { getApiErrorCode, getApiErrorMessage, isRecord } from '@/shared/utils/apiResponse';
import { apiDelete, apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';

import { fromFlowResponse, toFlowDefinitionPatch } from './fluxoEditorAdapter';
import { GATILHOS_LEXI } from '../catalogo/gatilhosLexi';
import type { ProblemaValidacao } from './fluxoValidacoes';
import type { FluxoArbitralEditavel } from '../types/editor';
import type {
  CapabilitiesMotor,
  CreateFluxoArbitralInput,
  FluxoArbitralListItem,
  FluxoArbitralListResponse,
  GatilhoFluxo,
  LexiAtiva,
  ModoAutoriaFluxo,
  RitoFluxo,
  VersaoFluxo,
} from '../types';

const ARBITRAL_SCENARIO = 'ARBITRAL';

interface RawFlowDefinition {
  nodes?: Array<{ id: string; type?: string; data?: Record<string, unknown> }>;
  metadata?: Record<string, unknown>;
}

export interface RawFlowItem {
  id: string;
  name: string;
  description?: string | null;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  scenarioType?: string | null;
  version?: number;
  definition?: RawFlowDefinition | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

interface RawListResponseEnvelope {
  data?: { items?: RawFlowItem[]; total?: number; page?: number; limit?: number };
  items?: RawFlowItem[];
  total?: number;
  page?: number;
  limit?: number;
}

interface RawFlowEnvelope {
  data?: RawFlowItem;
}

function extractRito(item: RawFlowItem): RitoFluxo | null {
  const meta = item.metadata ?? item.definition?.metadata ?? null;
  const candidate =
    meta && typeof meta === 'object' ? (meta as Record<string, unknown>).rito : undefined;
  if (
    candidate === 'SUMARIO' ||
    candidate === 'SUMARISSIMO' ||
    candidate === 'ORDINARIO' ||
    candidate === 'ARBITRIO'
  ) {
    return candidate;
  }
  return null;
}

function extractGatilho(item: RawFlowItem): GatilhoFluxo | null {
  const meta = item.metadata ?? item.definition?.metadata ?? null;
  if (!meta || typeof meta !== 'object') return null;
  const candidate = (meta as Record<string, unknown>).gatilho;
  if (candidate && typeof candidate === 'object') {
    const eventType = (candidate as Record<string, unknown>).eventType;
    if (typeof eventType === 'string' && eventType.length > 0) {
      return { tipo: 'EVENTO', eventType };
    }
  }
  return null;
}

function extractModoAutoria(item: RawFlowItem): ModoAutoriaFluxo {
  const meta = item.definition?.metadata ?? item.metadata ?? null;
  if (!meta || typeof meta !== 'object') return 'etapas';
  return (meta as Record<string, unknown>).modoAutoria === 'conversa' ? 'conversa' : 'etapas';
}

function lerEtapasEditorSource(item: RawFlowItem): Array<{ blocos?: unknown[] }> | null {
  const meta = item.metadata ?? item.definition?.metadata ?? null;
  if (!meta || typeof meta !== 'object') return null;
  const editorSource = (meta as Record<string, unknown>).editorSource;
  if (!editorSource || typeof editorSource !== 'object') return null;
  const etapas = (editorSource as Record<string, unknown>).etapas;
  return Array.isArray(etapas) ? (etapas as Array<{ blocos?: unknown[] }>) : null;
}

function contarPorNodesLegado(item: RawFlowItem): { etapas: number; blocos: number } {
  const nodes = item.definition?.nodes ?? [];
  const etapas = new Set<string>();
  for (const node of nodes) {
    const etapaId =
      node.data && typeof node.data === 'object'
        ? (node.data as Record<string, unknown>).etapaId
        : undefined;
    if (typeof etapaId === 'string' && etapaId.length > 0) {
      etapas.add(etapaId);
    }
  }
  return { etapas: etapas.size, blocos: nodes.length };
}

function contarEtapasEBlocos(item: RawFlowItem): { etapas: number; blocos: number } {
  const etapasEditor = lerEtapasEditorSource(item);
  if (etapasEditor) {
    const blocos = etapasEditor.reduce(
      (acc, etapa) => acc + (Array.isArray(etapa.blocos) ? etapa.blocos.length : 0),
      0,
    );
    return { etapas: etapasEditor.length, blocos };
  }
  return contarPorNodesLegado(item);
}

export function toListItem(raw: RawFlowItem): FluxoArbitralListItem {
  const { etapas, blocos } = contarEtapasEBlocos(raw);
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description ?? null,
    status: raw.status,
    rito: extractRito(raw),
    gatilho: extractGatilho(raw),
    modoAutoria: extractModoAutoria(raw),
    version: raw.version ?? 1,
    totalEtapas: etapas,
    totalBlocos: blocos,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

interface BackendProblema {
  codigo: string;
  nodeId?: string;
  nodeType?: string;
  mensagem: string;
}

function mapBackendProblema(p: BackendProblema): ProblemaValidacao {
  return { blocoId: p.nodeId, codigo: p.codigo, mensagem: p.mensagem };
}

/**
 * O core recusou a publicação por regra de validação (`FLOW_VALIDATION_FAILED`, core#2249/D-07):
 * leva os problemas para o editor juntá-los à lista do modal, em vez de só a mensagem longa.
 */
export class PublicacaoRecusadaError extends Error {
  constructor(
    message: string,
    public readonly problemas: ProblemaValidacao[],
  ) {
    super(message);
    this.name = 'PublicacaoRecusadaError';
  }
}

export class ConflitoFluxoError extends Error {
  constructor(public readonly versionAtual: number | null) {
    super('Fluxo alterado por outra sessão');
    this.name = 'ConflitoFluxoError';
  }
}

async function lerCorpoDaFalha(response: Response, fallback: string): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new Error(`${fallback} (status ${response.status})`);
  }
}

async function handleError(response: Response, fallback: string): Promise<never> {
  const body = await lerCorpoDaFalha(response, fallback);
  throw new Error(getApiErrorMessage(body, fallback));
}

function unwrapEnvelope<T extends object>(raw: { data?: T } | T): T {
  return 'data' in raw && raw.data ? raw.data : (raw as T);
}

function detailsDoEnvelope(body: unknown): Record<string, unknown> | null {
  if (!isRecord(body)) return null;
  const details = (isRecord(body.error) ? body.error.details : undefined) ?? body.details;
  return isRecord(details) ? details : null;
}

function extrairVersionConflito(body: unknown): number | null {
  const version = detailsDoEnvelope(body)?.version;
  return typeof version === 'number' ? version : null;
}

function ehBackendProblema(valor: unknown): valor is BackendProblema {
  return isRecord(valor) && typeof valor.codigo === 'string' && typeof valor.mensagem === 'string';
}

/**
 * Os problemas da recusa de publicação, ou `null` quando o corpo não é a recusa de validação do
 * `error-handler.plugin` do core (`{error:{code:'FLOW_VALIDATION_FAILED', details:{problemas}}}`).
 */
function problemasDaRecusa(body: unknown): ProblemaValidacao[] | null {
  if (getApiErrorCode(body) !== 'FLOW_VALIDATION_FAILED') return null;
  const problemas = detailsDoEnvelope(body)?.problemas;
  if (!Array.isArray(problemas)) return null;
  return problemas.filter(ehBackendProblema).map(mapBackendProblema);
}

/** Como `handleError`, mas a recusa de validação vira erro tipado com os problemas. */
async function falhaDaPublicacao(response: Response): Promise<never> {
  const fallback = 'Erro ao publicar fluxo';
  const body = await lerCorpoDaFalha(response, fallback);
  const mensagem = getApiErrorMessage(body, fallback);
  const problemas = problemasDaRecusa(body);
  if (problemas && problemas.length > 0) throw new PublicacaoRecusadaError(mensagem, problemas);
  throw new Error(mensagem);
}

export const fluxosArbitralService = {
  listar: async (filtros: {
    busca?: string;
    status?: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
    page?: number;
    limit?: number;
  }): Promise<FluxoArbitralListResponse> => {
    const params: Record<string, string | number | boolean> = {
      scenarioType: ARBITRAL_SCENARIO,
    };
    if (filtros.busca) params.search = filtros.busca;
    if (filtros.status) params.status = filtros.status;
    if (filtros.page) params.page = filtros.page;
    if (filtros.limit) params.limit = filtros.limit;
    const response = await apiGet('/admin/arbitral/flows', params);
    if (!response.ok) await handleError(response, 'Erro ao listar fluxos arbitrais');
    const body = (await response.json()) as RawListResponseEnvelope;
    const payload = body.data ?? body;
    const items = (payload.items ?? []).map(toListItem);
    return {
      items,
      total: payload.total ?? items.length,
      page: payload.page ?? 1,
      limit: payload.limit ?? 20,
    };
  },

  /**
   * Opções dos seletores "qual Lexi": todo fluxo ACTIVE com gatilho declarado, de todas as páginas.
   * Cada seletor filtra pelo gatilho do nó (`selectLexisAtivasPorGatilho`), porque o back resolve o
   * `lexiFlowId` por `metadata.gatilho.eventType` e fluxo de outro gatilho derruba o nó quando o
   * evento chega.
   */
  listarLexisAtivas: async (): Promise<LexiAtiva[]> => {
    const ativos = await listarTodasAsPaginas({ status: 'ACTIVE' });
    return ativos.flatMap((fluxo) =>
      fluxo.gatilho?.eventType
        ? [{ id: fluxo.id, name: fluxo.name, eventType: fluxo.gatilho.eventType }]
        : [],
    );
  },

  criar: async (input: CreateFluxoArbitralInput): Promise<FluxoArbitralListItem> => {
    const metadata = {
      ...(input.rito ? { rito: input.rito } : {}),
      modoAutoria: input.modoAutoria,
      gatilho: input.gatilho,
    };
    const body = {
      name: input.name,
      description: input.description,
      type: tipoDoFluxoNovo(input.gatilho),
      scenarioType: ARBITRAL_SCENARIO,
      definition: {
        nodes: [],
        connections: [],
        variables: [],
        metadata,
      },
      metadata,
      gatilho: input.gatilho,
    };
    const response = await apiPost('/admin/arbitral/flows', body);
    if (!response.ok) await handleError(response, 'Erro ao criar fluxo arbitral');
    const raw = (await response.json()) as RawFlowEnvelope | RawFlowItem;
    return toListItem(unwrapEnvelope(raw));
  },

  listarVersoes: async (fluxoId: string): Promise<VersaoFluxo[]> => {
    const response = await apiGet(`/admin/arbitral/flows/${fluxoId}/versions`, { limit: 50 });
    if (!response.ok) await handleError(response, 'Erro ao carregar histórico de versões');
    const raw = (await response.json()) as
      | { data?: { items?: VersaoFluxo[] } }
      | { items?: VersaoFluxo[] };
    return unwrapEnvelope(raw).items ?? [];
  },

  obterCapabilities: async (): Promise<CapabilitiesMotor> => {
    const response = await apiGet('/admin/arbitral/flows/capabilities');
    if (!response.ok) await handleError(response, 'Erro ao carregar capacidades do motor');
    const raw = (await response.json()) as
      | { data?: { nodeTypes?: string[]; features?: string[] } }
      | { nodeTypes?: string[]; features?: string[] };
    const { nodeTypes, features } = unwrapEnvelope(raw);
    // Core anterior aos recursos não manda `features`: vazio esconde o que depende deles.
    return { nodeTypes: nodeTypes ?? [], features: features ?? [] };
  },

  validar: async (fluxoId: string): Promise<ProblemaValidacao[]> => {
    const response = await apiPost(`/admin/arbitral/flows/${fluxoId}/validate`);
    if (!response.ok) await handleError(response, 'Erro ao validar fluxo');
    const raw = (await response.json()) as
      | { data?: { problemas?: BackendProblema[] } }
      | { problemas?: BackendProblema[] };
    return (unwrapEnvelope(raw).problemas ?? []).map(mapBackendProblema);
  },

  duplicar: async (fluxoId: string): Promise<FluxoArbitralListItem> => {
    const response = await apiPost(`/admin/arbitral/flows/${fluxoId}/duplicate`);
    if (!response.ok) await handleError(response, 'Erro ao duplicar fluxo');
    const raw = (await response.json()) as RawFlowEnvelope | RawFlowItem;
    return toListItem(unwrapEnvelope(raw));
  },

  publicar: async (fluxoId: string): Promise<FluxoArbitralListItem> => {
    const response = await apiPost(`/admin/arbitral/flows/${fluxoId}/publish`);
    if (!response.ok) await falhaDaPublicacao(response);
    const raw = (await response.json()) as RawFlowEnvelope | RawFlowItem;
    return toListItem(unwrapEnvelope(raw));
  },

  arquivar: async (fluxoId: string): Promise<FluxoArbitralListItem> => {
    const response = await apiPost(`/admin/arbitral/flows/${fluxoId}/archive`);
    if (!response.ok) await handleError(response, 'Erro ao arquivar fluxo');
    const raw = (await response.json()) as RawFlowEnvelope | RawFlowItem;
    return toListItem(unwrapEnvelope(raw));
  },

  renomear: async (fluxoId: string, name: string): Promise<FluxoArbitralListItem> => {
    const response = await apiPut(`/admin/arbitral/flows/${fluxoId}`, { name });
    if (!response.ok) await handleError(response, 'Erro ao renomear fluxo');
    const raw = (await response.json()) as RawFlowEnvelope | RawFlowItem;
    return toListItem(unwrapEnvelope(raw));
  },

  remover: async (fluxoId: string): Promise<void> => {
    const response = await apiDelete(`/admin/arbitral/flows/${fluxoId}`);
    if (!response.ok) await handleError(response, 'Erro ao remover fluxo');
  },

  obter: async (fluxoId: string): Promise<FluxoArbitralEditavel> => {
    const response = await apiGet(`/admin/arbitral/flows/${fluxoId}`);
    if (!response.ok) await handleError(response, 'Erro ao carregar fluxo');
    const raw = (await response.json()) as RawFlowEnvelope | RawFlowItem;
    return fromFlowResponse(unwrapEnvelope(raw));
  },

  salvarDefinition: async (fluxo: FluxoArbitralEditavel): Promise<FluxoArbitralEditavel> => {
    const patch = toFlowDefinitionPatch(fluxo);
    const response = await apiPut(`/admin/arbitral/flows/${fluxo.id}`, {
      name: fluxo.name,
      description: fluxo.description ?? undefined,
      definition: patch.definition,
      metadata: patch.metadata,
      startNodeId: patch.startNodeId,
      expectedVersion: fluxo.version,
    });
    if (response.status === 409) {
      const body: unknown = await response.json().catch(() => null);
      throw new ConflitoFluxoError(extrairVersionConflito(body));
    }
    if (!response.ok) await handleError(response, 'Erro ao salvar fluxo');
    const raw = (await response.json()) as RawFlowEnvelope | RawFlowItem;
    return fromFlowResponse(unwrapEnvelope(raw));
  },

  atualizarMetadados: async (
    fluxoId: string,
    patch: { name?: string; description?: string | null },
  ): Promise<FluxoArbitralEditavel> => {
    const response = await apiPut(`/admin/arbitral/flows/${fluxoId}`, patch);
    if (!response.ok) await handleError(response, 'Erro ao atualizar fluxo');
    const raw = (await response.json()) as RawFlowEnvelope | RawFlowItem;
    return fromFlowResponse(unwrapEnvelope(raw));
  },
};

/**
 * O fluxo do encaminhamento do trânsito nasce BASE (A-13, D-P3): fluxo arbitral não tem tela de
 * vínculo a escritório, então um CUSTOM com este gatilho ficava ACTIVE e o resolver do core
 * (escritório → BASE da empresa) nunca o achava. BASE vale para todos os escritórios da empresa —
 * escopo aceito na D-P3. Com mais de uma BASE ativa do gatilho o core usa a editada mais
 * recentemente, por isso a montagem do A-13 publica uma só. Os outros gatilhos seguem CUSTOM até
 * decisão própria.
 */
function tipoDoFluxoNovo(gatilho: GatilhoFluxo): 'BASE' | 'CUSTOM' {
  return gatilho.eventType === GATILHOS_LEXI.transito ? 'BASE' : 'CUSTOM';
}

const LIMITE_POR_PAGINA = 100;

/** Junta as páginas de `listar` até o `total` do back (ou até uma página vir vazia). */
async function listarTodasAsPaginas(filtros: {
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
}): Promise<FluxoArbitralListItem[]> {
  const todos: FluxoArbitralListItem[] = [];
  for (let page = 1; ; page += 1) {
    const { items, total } = await fluxosArbitralService.listar({
      ...filtros,
      page,
      limit: LIMITE_POR_PAGINA,
    });
    todos.push(...items);
    if (items.length === 0 || todos.length >= total) return todos;
  }
}
